import { useCallback, useMemo, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useListingLoader } from "../../../../components/hooks/ListingHooks";
import SelectionDeleter from "../../../../components/TableComponents/SelectionDeleter";
import ItemDeleter from "../../../../components/TableComponents/ItemDeleter";
import {
  ActionIcon,
  Button,
  ColorSwatch,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import dateColumn from "../../../../layouts/Table/columns/dateColumn";
import { nameColumn } from "../../../../layouts/Table/columns/nameColumn";
import MainTable from "../../../../layouts/Table/MainTable";
import { deleteTag, editTag, getTags } from "../../../../api/TagsAPI";
import { sharingColumn } from "../../../../layouts/Table/columns/sharingColumn";
import SelectionSharer from "../../../../components/TableComponents/SelectionSharer";
import { IconArrowLeft, IconPlus } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import TagsColorEditor from "../../../../layouts/Table/columns/tagsColorEditor";

const TagsListing = () => {
  const apiRef = useGridApiRef();
  const lang = useLanguage((s) => s.language);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const columns = useMemo(
    () => [
      nameColumn({
        handleClick: () => {},
        disableLink: true,
        editFunction: () => editTag,
        getDisabled: () => false,
        width: 600,
        apiRef,
      }),
      {
        field: "color",
        headerName: lang.prm.status.color,
        renderCell: ({ row }) => (
          <Group>
            <TagsColorEditor
              tag={row}
              isSwatch
              onFinished={() => setLoading(true)}
            />
          </Group>
        ),
      },
      dateColumn(),
      sharingColumn({ apiRef, editFunction: () => editTag }),
    ],
    [apiRef]
  );

  const actionsCell = useCallback(
    ({ row }) => {
      return (
        <ItemDeleter
          title_bold={`${row.name} #${row.id}`}
          handleDelete={deleteTag}
          deletedMessage={lang.tags.delete_success}
          id={row.id}
          apiRef={apiRef}
        />
      );
    },
    [apiRef, lang.tags.delete_success]
  );

  const toolbarActions = useCallback(
    ({ selectedIds }) => (
      <>
        <SelectionDeleter
          handleDelete={deleteTag}
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
        />

        <SelectionSharer
          handleShare={editTag}
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
        />
      </>
    ),
    [apiRef]
  );

  useListingLoader({
    loading,
    setLoading,
    listGetter: getTags,
    responseField: "tags_list",
    setRows,
  });

  return (
    <Stack flex={1} pr={48} pt={16} gap={0}>
      <Group gap={"md"}>
        <ActionIcon size={"xl"} component={Link} to={"/settings/tools"}>
          <IconArrowLeft />
        </ActionIcon>
        <Text size="xl" fw={600} mr={"auto"}>
          {lang.settings.tool_tags}
        </Text>
        <Button
          leftSection={<IconPlus size={18} />}
          radius={"md"}
          size="md"
          color="primary"
          component={Link}
          to={"/settings/tools/tags/create"}
        >
          {lang.tags.create.button_tooltip}
        </Button>
      </Group>

      <Divider my={16} />

      <Flex h={"calc(100vh - 196px)"}>
        <MainTable
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          loading={loading}
          setLoading={setLoading}
          id={"list-table"}
          toolbarActions={toolbarActions}
          actionsCell={actionsCell}
          actionsCellWidth={50}
          whiteButtons
          tableSx={{
            overflow: "hidden",
            "& .MuiDataGrid-pinnedColumns": {
              background: "#fff",
              borderTopLeftRadius: 3,
              border: "none",
            },
            "& .MuiDataGrid-pinnedColumnHeaders": {
              background: "#fff",
              border: "none",
            },
            "& .table-search": {
              width: "300px !important",
            },
          }}
        />
      </Flex>
    </Stack>
  );
};

export default TagsListing;
